import React, { useState, useEffect, useRef } from 'react';
import { Map as OLMap, View } from 'ol';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Draw, Modify, Snap, defaults as defaultInteractions } from 'ol/interaction';
import { Circle as CircleGeometry, Polygon, Point } from 'ol/geom';
import { Style, Fill, Stroke, Circle as CircleStyle } from 'ol/style';
import { getArea } from 'ol/sphere';
import { fromLonLat } from 'ol/proj';
import { Zoom } from 'ol/control';
import { useStore } from '../../store';
import { GIS, ENABLE_ACTIONS, SET_HAS_POLYGON } from '../../actions/map.actions';
import { trackFunnelStep, FunnelSteps } from '../../lib/analytics';
import { useToasts } from 'react-toast-notifications';
import MapSearch from '../MapSearch';
import { KmlUploadButton, KmlPolygonPicker } from '../KmlUpload';
import { parseKmlFile, candidateToFeature } from '../../lib/kmlImport';
import 'ol/ol.css';
import './CustomMap.css';

// Estilo para las features dibujadas (fuera del componente para evitar recreación)
const featureStyle = new Style({
    fill: new Fill({
        color: 'rgba(78, 205, 196, 0.2)',
    }),
    stroke: new Stroke({
        color: '#4ECDC4',
        width: 2,
    }),
    image: new CircleStyle({
        radius: 5,
        fill: new Fill({
            color: '#4ECDC4',
        }),
    }),
});

const MIN_FIELD_HA = 5;
const MAX_FIELD_HA = 100;
const START_DRAW_MESSAGE = 'Hacé click en el mapa para colocar el primer punto de tu lote.';

/** Area feedback while drawing — min warning only after user once reached MIN_FIELD_HA. */
function getDrawingAreaFeedback(area, reachedMinArea) {
    if (area > MAX_FIELD_HA) {
        return {
            message: `Área: ${area.toFixed(2)} ha - Máximo permitido: ${MAX_FIELD_HA} ha`,
            isError: true,
            canFinish: false,
            reachedMinArea,
        };
    }
    if (area >= MIN_FIELD_HA) {
        return {
            message: `Área: ${area.toFixed(2)} ha\nSeleccioná el primer punto para terminar.`,
            isError: false,
            canFinish: true,
            reachedMinArea: true,
        };
    }
    if (area > 0 && reachedMinArea) {
        return {
            message: `Área: ${area.toFixed(2)} ha - Mínimo permitido: ${MIN_FIELD_HA} ha`,
            isError: true,
            canFinish: false,
            reachedMinArea,
        };
    }
    if (area > 0) {
        return {
            message: `Área: ${area.toFixed(2)} ha - Seguí agregando puntos`,
            isError: false,
            canFinish: false,
            reachedMinArea,
        };
    }
    return {
        message: 'Seleccioná el próximo punto',
        isError: false,
        canFinish: false,
        reachedMinArea,
    };
}

function isDrawingAreaInvalid(area, reachedMinArea) {
    if (area <= 0) return false;
    if (area > MAX_FIELD_HA) return true;
    return reachedMinArea && area < MIN_FIELD_HA;
}


const CustomMap = ({ isExpanded = false, onPolygonConfirmed }) => {
    const [creation, setCreation] = useState(false);
    const [{ map }, dispatch] = useStore();
    const { addToast } = useToasts();
    const [drawMessage, setDrawMessage] = useState('');
    const [showDrawMessage, setShowDrawMessage] = useState(false);
    const [drawMessageError, setDrawMessageError] = useState(false);
    const [activeTool, setActiveTool] = useState(null);
    const [drawingFeature, setDrawingFeature] = useState(null);
    const [hasFeatures, setHasFeatures] = useState(false);
    const [canFinishDrawing, setCanFinishDrawing] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [undoStackLength, setUndoStackLength] = useState(0);
    const [kmlLoading, setKmlLoading] = useState(false);
    const [kmlCandidates, setKmlCandidates] = useState([]);
    const [kmlTruncated, setKmlTruncated] = useState(0);
    const [searchExpanded, setSearchExpanded] = useState(false);
    const mapRef = useRef(null);
    const olMapRef = useRef(null);
    const vectorSourceRef = useRef(null);
    const vectorLayerRef = useRef(null);
    const drawInteractionRef = useRef(null);
    const modifyInteractionRef = useRef(null);
    const snapInteractionRef = useRef(null);
    const currentFeatureRef = useRef(null);
    const editGeometryChangeKeyRef = useRef(null);
    const editingFeatureRef = useRef(null);
    const undoStackRef = useRef([]);
    const isFirstMoveEndRef = useRef(true);
    const isDrawingRef = useRef(false);
    const drawAreaValidRef = useRef(false);
    const drawReachedMinAreaRef = useRef(false);
    const currentDrawingFeatureRef = useRef(null);

    // Calcular área en hectáreas
    const calculateArea = (geometry) => {
        if (geometry instanceof CircleGeometry) {
            const radius = geometry.getRadius();
            const area = Math.PI * radius * radius;
            return area / 10000; // Convertir a hectáreas
        } else if (geometry instanceof Polygon) {
            const area = getArea(geometry);
            return area / 10000; // Convertir a hectáreas
        }
        return 0;
    };

    const scrollToFormStep2 = () => {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }

        const input = document.getElementById('nombre');
        if (input) {
            setTimeout(() => {
                input.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setTimeout(() => input.focus(), 400);
            }, 200);
        }
    };

    const isExpandedRef = useRef(isExpanded);
    const onPolygonConfirmedRef = useRef(onPolygonConfirmed);
    const goToFormAfterConfirmRef = useRef(() => {});

    isExpandedRef.current = isExpanded;
    onPolygonConfirmedRef.current = onPolygonConfirmed;
    goToFormAfterConfirmRef.current = () => {
        if (isExpandedRef.current && onPolygonConfirmedRef.current) {
            onPolygonConfirmedRef.current();
            return;
        }
        scrollToFormStep2();
    };

    const goToFormAfterConfirm = () => {
        goToFormAfterConfirmRef.current();
    };

    const clearMapForImport = () => {
        setActiveTool(null);
        setDrawingFeature(null);
        setShowDeleteConfirm(false);
        setCanFinishDrawing(false);
        drawReachedMinAreaRef.current = false;

        if (drawInteractionRef.current && olMapRef.current) {
            olMapRef.current.removeInteraction(drawInteractionRef.current);
            drawInteractionRef.current = null;
        }
        if (modifyInteractionRef.current && olMapRef.current) {
            olMapRef.current.removeInteraction(modifyInteractionRef.current);
            modifyInteractionRef.current = null;
        }

        if (vectorSourceRef.current) {
            vectorSourceRef.current.clear();
        }
        currentFeatureRef.current = null;
        dispatch({ type: GIS, payload: null });
        dispatch({ type: SET_HAS_POLYGON, payload: false });
    };

    const applyImportedPolygon = (candidate) => {
        if (!vectorSourceRef.current || !olMapRef.current) return;

        clearMapForImport();

        const feature = candidateToFeature(candidate);
        vectorSourceRef.current.addFeature(feature);

        const extent = feature.getGeometry().getExtent();
        olMapRef.current.getView().fit(extent, {
            padding: [48, 48, 48, 48],
            maxZoom: 17,
            duration: 500,
        });

        checkArea(feature);
        trackFunnelStep(FunnelSteps.KML_POLYGON_SELECTED, {
            hectares: Math.round(candidate.hectares),
        });
        goToFormAfterConfirm();
        setKmlCandidates([]);
        setKmlTruncated(0);
    };

    const handleKmlFile = async (file) => {
        setKmlLoading(true);
        trackFunnelStep(FunnelSteps.KML_UPLOAD_STARTED);

        try {
            const result = await parseKmlFile(file);

            if (result.valid.length === 1) {
                applyImportedPolygon(result.valid[0]);
                addToast('Polígono importado correctamente.', {
                    appearance: 'success',
                    autoDismiss: true,
                });
            } else {
                setKmlCandidates(result.valid);
                setKmlTruncated(Math.max(0, result.validTotal - result.valid.length));
                if (result.invalidCount > 0) {
                    addToast(
                        `${result.invalidCount} polígono(s) fuera del rango 5–100 ha fueron omitidos.`,
                        { appearance: 'info', autoDismiss: true },
                    );
                }
            }
        } catch (err) {
            const messages = {
                FILE_TOO_LARGE: 'El archivo supera el límite de 50 MB.',
                NO_KML: 'No se encontró un archivo KML dentro del KMZ.',
                INVALID_EXT: 'Formato no válido. Usá archivos .kml o .kmz.',
                NO_POLYGONS: 'No se encontraron polígonos en el archivo.',
                NO_VALID_POLYGON: 'Ningún polígono cumple entre 5 y 100 ha.',
            };
            addToast(messages[err.message] || 'No se pudo leer el archivo.', {
                appearance: 'warning',
                autoDismiss: true,
            });
        } finally {
            setKmlLoading(false);
        }
    };

    const invalidateFieldIfOutOfRange = (feature) => {
        if (!feature) return;
        const area = calculateArea(feature.getGeometry());
        if (area < 5 || area > 100) {
            dispatch({ type: GIS, payload: null });
        }
    };

    const getMapFeature = () =>
        currentFeatureRef.current || vectorSourceRef.current?.getFeatures()[0] || null;

    const showFinishBlockedMessage = () => {
        setDrawMessage(
            `El área debe estar entre ${MIN_FIELD_HA} y ${MAX_FIELD_HA} ha para terminar. Ajustá el polígono y cerrá en el punto ámbar cuando cumpla las medidas.`,
        );
        setShowDrawMessage(true);
        setDrawMessageError(true);
    };

    const finishInvalidPolygon = (feature, area) => {
        currentFeatureRef.current = feature;
        dispatch({ type: GIS, payload: null });

        if (area > MAX_FIELD_HA) {
            setDrawMessage(`Área: ${area.toFixed(2)} ha - Máximo permitido: ${MAX_FIELD_HA} ha`);
        } else {
            setDrawMessage(`Área: ${area.toFixed(2)} ha - Mínimo permitido: ${MIN_FIELD_HA} ha`);
        }
        setDrawMessageError(true);
        setShowDrawMessage(true);
    };

    // Validar área y guardar feature
    const checkArea = (feature) => {
        const geometry = feature.getGeometry();
        const area = calculateArea(geometry);
        
        if (area < 5 || area > 100) {
            // Solo eliminar la feature, sin mostrar toast
            vectorSourceRef.current.removeFeature(feature);
            currentFeatureRef.current = null;
            dispatch({
                type: GIS,
                payload: null
            });
        } else {
            currentFeatureRef.current = feature;
            dispatch({
                type: GIS,
                payload: feature
            });
            trackFunnelStep(FunnelSteps.FIELD_SELECTED);
            trackFunnelStep(FunnelSteps.FIELD_DRAW_COMPLETED, { hectares: Math.round(area) });
        }
    };

    // Inicializar el mapa
    useEffect(() => {
        if (!mapRef.current) return;

        // Crear fuente vectorial para las features dibujadas
        const vectorSource = new VectorSource();
        vectorSourceRef.current = vectorSource;
        
        // Escuchar cambios en las features para actualizar el estado
        const syncHasPolygon = () => {
            dispatch({ type: SET_HAS_POLYGON, payload: vectorSource.getFeatures().length > 0 });
        };

        vectorSource.on('addfeature', (e) => {
            setHasFeatures(vectorSource.getFeatures().length > 0);
            if (!currentFeatureRef.current && e.feature) {
                currentFeatureRef.current = e.feature;
            }
            syncHasPolygon();
        });
        vectorSource.on('removefeature', () => {
            setHasFeatures(vectorSource.getFeatures().length > 0);
            syncHasPolygon();
        });
        vectorSource.on('clear', () => {
            setHasFeatures(false);
            syncHasPolygon();
        });

        // Función de estilo que muestra vértices cuando está en modo edición
        const vectorLayerStyleFunction = (feature) => {
            const styles = [
                new Style({
                    fill: new Fill({
                        color: 'rgba(78, 205, 196, 0.2)',
                    }),
                    stroke: new Stroke({
                        color: '#4ECDC4',
                        width: 2,
                    }),
                })
            ];
            
            // Si hay una interacción de modify activa, mostrar los vértices
            const geometry = feature.getGeometry();
            if (geometry instanceof Polygon) {
                const coordinates = geometry.getCoordinates()[0];
                coordinates.forEach((coord) => {
                    styles.push(
                        new Style({
                            image: new CircleStyle({
                                radius: 6,
                                fill: new Fill({
                                    color: '#4ECDC4',
                                }),
                                stroke: new Stroke({
                                    color: '#fff',
                                    width: 2,
                                }),
                            }),
                            geometry: new Point(coord),
                        })
                    );
                });
            } else if (geometry instanceof CircleGeometry) {
                const center = geometry.getCenter();
                styles.push(
                    new Style({
                        image: new CircleStyle({
                            radius: 6,
                            fill: new Fill({
                                color: '#4ECDC4',
                            }),
                            stroke: new Stroke({
                                color: '#fff',
                                width: 2,
                            }),
                        }),
                        geometry: new Point(center),
                    })
                );
            }
            
            return styles;
        };

        const vectorLayer = new VectorLayer({
            source: vectorSource,
            style: featureStyle, // Estilo por defecto
        });
        
        vectorLayerRef.current = vectorLayer;

        // Crear el mapa
        const olMap = new OLMap({
            target: mapRef.current,
            layers: [
                new TileLayer({
                    source: new XYZ({
                        url: 'http://mt0.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
                        attributions: 'Google',
                    }),
                }),
                vectorLayer,
            ],
            view: new View({
                center: fromLonLat([-61.23226293159145,
                    -21.627860421164186]),
                zoom: 3.5,
            }),
            controls: [], // El zoom se manejará manualmente en el toolbar
            interactions: defaultInteractions({
                doubleClickZoom: false,
                dragRotate: false,
                pinchRotate: false,
                keyboard: false,
            }),
        });

        olMapRef.current = olMap;

        // Crear interacción de snap
        const snap = new Snap({
            source: vectorSource,
        });
        snapInteractionRef.current = snap;
        olMap.addInteraction(snap);

        // Activar herramienta de dibujo cuando el usuario interactúa con el mapa (zoom, pan) y no hay polígono
        const handleMoveEnd = () => {
            // Ignorar el primer moveend que dispara OpenLayers al cargar el mapa
            if (isFirstMoveEndRef.current) {
                isFirstMoveEndRef.current = false;
                return;
            }
            if (vectorSourceRef.current && vectorSourceRef.current.getFeatures().length === 0) {
                setActiveTool('polygon');
            }
        };
        olMap.on('moveend', handleMoveEnd);

        // Manejar resize
        const handleResize = () => {
            olMap.updateSize();
        };
        window.addEventListener('resize', handleResize);

        const resizeObserver = new ResizeObserver(() => {
            olMap.updateSize();
        });
        if (mapRef.current) {
            resizeObserver.observe(mapRef.current);
        }

        return () => {
            olMap.un('moveend', handleMoveEnd);
            window.removeEventListener('resize', handleResize);
            resizeObserver.disconnect();
            olMap.setTarget(null);
        };
    }, []);

    // Manejar cambios en el mapa del store
    useEffect(() => {
        if (map && !creation) {
            dispatch({ type: ENABLE_ACTIONS });
            setCreation(true);
        }
    }, [map, creation, dispatch]);

    useEffect(() => {
        if (!olMapRef.current) return undefined;
        const timer = window.setTimeout(() => {
            olMapRef.current?.updateSize();
        }, 200);
        return () => window.clearTimeout(timer);
    }, [isExpanded]);

    // Manejar herramientas de dibujo
    useEffect(() => {
        if (!olMapRef.current || !vectorSourceRef.current) return;

        const olMap = olMapRef.current;
        const vectorSource = vectorSourceRef.current;

        // Remover interacciones anteriores
        if (drawInteractionRef.current) {
            olMap.removeInteraction(drawInteractionRef.current);
            drawInteractionRef.current = null;
        }

        if (modifyInteractionRef.current) {
            olMap.removeInteraction(modifyInteractionRef.current);
            modifyInteractionRef.current = null;
        }

        // Si hay una herramienta activa, crear la interacción correspondiente
        if (activeTool === 'polygon') {
            setDrawMessage(START_DRAW_MESSAGE);
            setShowDrawMessage(true);

            // Función de estilo personalizada para mostrar vértices y área
            let currentDrawingFeature = null;
            const drawStyleFunction = (feature) => {
                const geometry = feature.getGeometry();
                if (!geometry) return [];
                
                // Calcular área
                let area = 0;
                if (geometry instanceof Polygon) {
                    area = getArea(geometry) / 10000; // Convertir a hectáreas
                }
                
                const isInvalid = isDrawingAreaInvalid(area, drawReachedMinAreaRef.current);
                const fillColor = isInvalid ? 'rgba(255, 107, 107, 0.2)' : 'rgba(78, 205, 196, 0.2)';
                const strokeColor = isInvalid ? '#FF6B6B' : '#4ECDC4';
                
                const styles = [
                    new Style({
                        fill: new Fill({
                            color: fillColor,
                        }),
                        stroke: new Stroke({
                            color: strokeColor,
                            width: 2,
                        }),
                    })
                ];
                
                if (geometry instanceof Polygon) {
                    const coordinates = geometry.getCoordinates()[0];

                    let firstCoord = null;
                    
                    // Agregar estilo para todos los vértices excepto el primero
                    coordinates.forEach((coord, index) => {
                        if (index === 0) {
                            firstCoord = coord;
                            return;
                        }

                        const radius = 6;
                        const fillColorVertex = isInvalid ? '#FF6B6B' : '#4ECDC4';
                        
                        styles.push(
                            new Style({
                                image: new CircleStyle({
                                    radius,
                                    fill: new Fill({
                                        color: fillColorVertex,
                                    }),
                                    stroke: new Stroke({
                                        color: '#fff',
                                        width: 2,
                                    }),
                                }),
                                geometry: new Point(coord),
                            })
                        );
                    });

                    // Dibujar el primer punto al final, para que quede por encima
                    if (firstCoord) {
                        styles.push(
                            new Style({
                                image: new CircleStyle({
                                    radius: 9,
                                    fill: new Fill({
                                        color: '#FFA000', // punto inicial en color ámbar
                                    }),
                                    stroke: new Stroke({
                                        color: '#fff',
                                        width: 3,
                                    }),
                                }),
                                geometry: new Point(firstCoord),
                            })
                        );
                    }
                }
                
                return styles;
            };

            const draw = new Draw({
                source: vectorSource,
                type: 'Polygon',
                style: drawStyleFunction,
                finishCondition: () => {
                    if (!drawAreaValidRef.current) {
                        showFinishBlockedMessage();
                        return false;
                    }
                    return true;
                },
                condition: (evt) => {
                    if (!isDrawingRef.current || drawAreaValidRef.current) return true;
                    const feature = currentDrawingFeatureRef.current;
                    if (!feature) return true;
                    const geom = feature.getGeometry();
                    if (!geom || !(geom instanceof Polygon)) return true;
                    const ring = geom.getCoordinates()[0];
                    if (ring.length < 3) return true;
                    const firstCoord = ring[0];
                    const firstPixel = olMap.getPixelFromCoordinate(firstCoord);
                    const clickPixel = evt.pixel;
                    const dist = Math.hypot(firstPixel[0] - clickPixel[0], firstPixel[1] - clickPixel[1]);
                    if (dist < 25) {
                        showFinishBlockedMessage();
                        return false;
                    }
                    return true;
                },
            });

            // Variables para almacenar la key del listener fuera del scope del evento
            let geometryChangeKey = null;
            let currentGeometry = null;
            
            draw.on('drawstart', (e) => {
                isDrawingRef.current = true;
                drawReachedMinAreaRef.current = false;
                currentDrawingFeatureRef.current = e.feature;
                currentDrawingFeature = e.feature;
                setDrawingFeature(e.feature);
                setDrawMessage('Seleccioná el próximo punto');
                dispatch({
                    type: GIS,
                    payload: null
                });
                
                // Limpiar listener anterior si existe
                if (geometryChangeKey !== null && currentGeometry) {
                    try {
                        currentGeometry.un(geometryChangeKey);
                    } catch (e) {
                        // Ignorar errores
                    }
                }
                
                // Escuchar cambios en la geometría para actualizar el área en tiempo real
                const feature = e.feature;
                currentGeometry = feature.getGeometry();
                
                const updateArea = () => {
                    const geometry = feature.getGeometry();
                    if (geometry instanceof Polygon) {
                        const coordinates = geometry.getCoordinates()[0];
                        if (coordinates.length >= 3) {
                            const area = getArea(geometry) / 10000;
                            const feedback = getDrawingAreaFeedback(area, drawReachedMinAreaRef.current);
                            drawReachedMinAreaRef.current = feedback.reachedMinArea;
                            drawAreaValidRef.current = feedback.canFinish;
                            setDrawMessage(feedback.message);
                            setDrawMessageError(feedback.isError);
                            setCanFinishDrawing(feedback.canFinish);
                            feature.changed();
                        } else {
                            drawAreaValidRef.current = false;
                            setCanFinishDrawing(false);
                            setDrawMessage('Seleccioná el próximo punto');
                            setDrawMessageError(false);
                        }
                    }
                };
                
                // Escuchar cambios en la geometría
                geometryChangeKey = currentGeometry.on('change', updateArea);
                
                // Limpiar listener cuando termine el dibujo
                const cleanupListener = () => {
                    isDrawingRef.current = false;
                    drawReachedMinAreaRef.current = false;
                    currentDrawingFeatureRef.current = null;
                    if (geometryChangeKey !== null && currentGeometry) {
                        try {
                            currentGeometry.un(geometryChangeKey);
                        } catch (e) {
                            // Ignorar errores si el listener ya fue removido
                            console.warn('Error al remover listener:', e);
                        }
                    }
                    geometryChangeKey = null;
                    currentGeometry = null;
                    setDrawingFeature(null);
                };
                
                draw.once('drawend', cleanupListener);
                draw.once('drawabort', cleanupListener);
            });

            draw.on('drawend', (e) => {
                const feature = e.feature;
                const geometry = feature.getGeometry();
                const cleanupDraw = () => {
                    setCanFinishDrawing(false);
                    setActiveTool(null);
                    olMap.removeInteraction(draw);
                    drawInteractionRef.current = null;
                };

                if (geometry instanceof Polygon) {
                    const area = getArea(geometry) / 10000;
                    if (area > MAX_FIELD_HA || area < MIN_FIELD_HA) {
                        // drawend fires before OL adds the feature to the source — keep it for editing
                        finishInvalidPolygon(feature, area);
                        cleanupDraw();
                    } else {
                        checkArea(feature);
                        setShowDrawMessage(false);
                        setDrawMessage('');
                        setDrawMessageError(false);
                        cleanupDraw();
                        goToFormAfterConfirm();
                    }
                }
            });

            draw.on('drawabort', () => {
                setDrawingFeature(null);
                setCanFinishDrawing(false);
                setDrawMessageError(false);
                drawReachedMinAreaRef.current = false;
                setDrawMessage(START_DRAW_MESSAGE);
                setShowDrawMessage(true);
            });

            olMap.addInteraction(draw);
            drawInteractionRef.current = draw;

        } else if (activeTool === 'edit') {
            setShowDrawMessage(true);
            setDrawMessage('Arrastrá los puntos del polígono para ajustarlo.');

            // Obtener la feature actual para mostrar su área
            const currentFeature = getMapFeature();
            if (currentFeature) {
                currentFeatureRef.current = currentFeature;
                editingFeatureRef.current = currentFeature;
                const geometry = currentFeature.getGeometry();
                const area = calculateArea(geometry);
                if (area > 100) {
                    setDrawMessage(`Área: ${area.toFixed(2)} ha - Máximo permitido: 100 ha`);
                    setDrawMessageError(true);
                } else if (area < 5) {
                    setDrawMessage(`Área: ${area.toFixed(2)} ha - Mínimo permitido: 5 ha`);
                    setDrawMessageError(true);
                } else {
                    setDrawMessage(`Área: ${area.toFixed(2)} ha`);
                    setDrawMessageError(false);
                }
            }

            // Cambiar el estilo de la capa para mostrar todos los vértices
            if (vectorLayerRef.current) {
                const editStyleFunction = (feature) => {
                    const geometry = feature.getGeometry();
                    const area = calculateArea(geometry);
                    const isOverLimit = area > 100;
                    const isUnderLimit = area < 5;
                    const isInvalid = isOverLimit || isUnderLimit;
                    const fillColor = isInvalid ? 'rgba(255, 107, 107, 0.2)' : 'rgba(78, 205, 196, 0.2)';
                    const strokeColor = isInvalid ? '#FF6B6B' : '#4ECDC4';
                    const vertexColor = isInvalid ? '#FF6B6B' : '#4ECDC4';
                    
                    const styles = [
                        new Style({
                            fill: new Fill({
                                color: fillColor,
                            }),
                            stroke: new Stroke({
                                color: strokeColor,
                                width: 2,
                            }),
                        })
                    ];
                    
                    if (geometry instanceof Polygon) {
                        const coordinates = geometry.getCoordinates()[0];
                        coordinates.forEach((coord) => {
                            styles.push(
                                new Style({
                                    image: new CircleStyle({
                                        radius: 6,
                                        fill: new Fill({
                                            color: vertexColor,
                                        }),
                                        stroke: new Stroke({
                                            color: '#fff',
                                            width: 2,
                                        }),
                                    }),
                                    geometry: new Point(coord),
                                })
                            );
                        });
                    } else if (geometry instanceof CircleGeometry) {
                        const center = geometry.getCenter();
                        styles.push(
                            new Style({
                                image: new CircleStyle({
                                    radius: 6,
                                    fill: new Fill({
                                        color: vertexColor,
                                    }),
                                    stroke: new Stroke({
                                        color: '#fff',
                                        width: 2,
                                    }),
                                }),
                                geometry: new Point(center),
                            })
                        );
                    }
                    
                    return styles;
                };
                vectorLayerRef.current.setStyle(editStyleFunction);
                // Forzar actualización del mapa
                if (olMapRef.current) {
                    vectorSource.changed();
                }
            }

            // Estilo para los vértices editables en Modify
            const modifyVertexStyle = new Style({
                image: new CircleStyle({
                    radius: 6,
                    fill: new Fill({
                        color: '#4ECDC4',
                    }),
                    stroke: new Stroke({
                        color: '#fff',
                        width: 2,
                    }),
                }),
            });

            const modify = new Modify({
                source: vectorSource,
                style: modifyVertexStyle,
            });

            modify.on('modifystart', (e) => {
                const features = e.features.getArray();
                if (features.length > 0) {
                    const feature = features[0];
                    editingFeatureRef.current = feature;
                    // Guardar geometría actual en la pila para poder deshacer (múltiples acciones)
                    undoStackRef.current.push(feature.getGeometry().clone());
                    setUndoStackLength(undoStackRef.current.length);

                    // Limpiar listener anterior si existe
                    if (editGeometryChangeKeyRef.current && feature.getGeometry()) {
                        try {
                            feature.getGeometry().un(editGeometryChangeKeyRef.current);
                        } catch (e) {
                            // Ignorar errores
                        }
                    }
                    
                    // Escuchar cambios en la geometría para actualizar el área en tiempo real
                    const geometry = feature.getGeometry();
                    const updateArea = () => {
                        const currentGeometry = feature.getGeometry();
                        if (currentGeometry) {
                            const area = calculateArea(currentGeometry);
                            if (area > 100) {
                                setDrawMessage(`Área: ${area.toFixed(2)} ha - Máximo permitido: 100 ha`);
                                setDrawMessageError(true);
                                invalidateFieldIfOutOfRange(feature);
                            } else if (area < 5) {
                                setDrawMessage(`Área: ${area.toFixed(2)} ha - Mínimo permitido: 5 ha`);
                                setDrawMessageError(true);
                                invalidateFieldIfOutOfRange(feature);
                            } else {
                                setDrawMessage(`Área: ${area.toFixed(2)} ha`);
                                setDrawMessageError(false);
                            }
                            // Actualizar el estilo para reflejar el cambio de color
                            if (vectorLayerRef.current) {
                                vectorSource.changed();
                            }
                        }
                    };
                    
                    // Escuchar cambios en la geometría
                    editGeometryChangeKeyRef.current = geometry.on('change', updateArea);
                    
                    // Actualizar área inicial
                    updateArea();
                }
            });

            modify.on('modifyend', (e) => {
                // No guardar automáticamente, solo actualizar el área
                const features = e.features.getArray();
                if (features.length > 0) {
                    const feature = features[0];
                    const area = calculateArea(feature.getGeometry());
                    if (area > 100) {
                        setDrawMessage(`Área: ${area.toFixed(2)} ha - Máximo permitido: 100 ha`);
                        setDrawMessageError(true);
                        invalidateFieldIfOutOfRange(feature);
                    } else if (area < 5) {
                        setDrawMessage(`Área: ${area.toFixed(2)} ha - Mínimo permitido: 5 ha`);
                        setDrawMessageError(true);
                        invalidateFieldIfOutOfRange(feature);
                    } else {
                        setDrawMessage(`Área: ${area.toFixed(2)} ha`);
                        setDrawMessageError(false);
                    }
                    // Actualizar el estilo
                    if (vectorLayerRef.current) {
                        vectorSource.changed();
                    }
                }
            });

            olMap.addInteraction(modify);
            modifyInteractionRef.current = modify;

            // Evitar zoom del mapa al hacer doble click mientras se edita
            const handleDoubleClick = (e) => {
                e.preventDefault();
            };

            olMap.on('dblclick', handleDoubleClick);

            // Restaurar el estilo cuando se desactiva la edición
            return () => {
                // Limpiar listener de geometría
                if (editGeometryChangeKeyRef.current && editingFeatureRef.current) {
                    try {
                        const geometry = editingFeatureRef.current.getGeometry();
                        if (geometry) {
                            geometry.un(editGeometryChangeKeyRef.current);
                        }
                    } catch (e) {
                        // Ignorar errores
                    }
                    editGeometryChangeKeyRef.current = null;
                }
                editingFeatureRef.current = null;
                
                if (vectorLayerRef.current) {
                    vectorLayerRef.current.setStyle(featureStyle);
                }
                olMap.un('dblclick', handleDoubleClick);
                // Ocultar mensaje y vaciar pila de deshacer al salir del modo edición
                setShowDrawMessage(false);
                setDrawMessage('');
                setDrawMessageError(false);
                undoStackRef.current = [];
                setUndoStackLength(0);
            };

        } else if (activeTool === 'delete') {
            setShowDrawMessage(false);
            setDrawMessage('');

            // Limpiar todo el mapa
            const allFeatures = vectorSource.getFeatures();
            allFeatures.forEach(feature => {
                vectorSource.removeFeature(feature);
            });
            
            currentFeatureRef.current = null;
            dispatch({
                type: GIS,
                payload: null
            });
            
            // Desactivar la herramienta después de eliminar
            setActiveTool(null);
        } else {
            olMap.getViewport().style.cursor = '';
            setShowDrawMessage(false);
            setDrawMessage('');
            setDrawMessageError(false);
            // Restaurar el estilo cuando no hay herramienta activa
            if (vectorLayerRef.current) {
                vectorLayerRef.current.setStyle(featureStyle);
            }
        }

        return () => {
            if (drawInteractionRef.current) {
                olMap.removeInteraction(drawInteractionRef.current);
            }
            if (modifyInteractionRef.current) {
                olMap.removeInteraction(modifyInteractionRef.current);
            }
            // Restaurar el estilo al desmontar
            if (vectorLayerRef.current) {
                vectorLayerRef.current.setStyle(featureStyle);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTool, dispatch, addToast]);

    // Manejar eliminación desde fuera — only when polygon flag is cleared explicitly (e.g. delete)
    useEffect(() => {
        if (map && !map.hasPolygon && currentFeatureRef.current) {
            currentFeatureRef.current = null;
        }
    }, [map]);

    const handleToolClick = (tool) => {
        if (activeTool === tool) {
            setActiveTool(null);
        } else {
            if (tool === 'edit') {
                const feature = getMapFeature();
                if (feature) {
                    currentFeatureRef.current = feature;
                }
            }
            if (tool === 'polygon') {
                trackFunnelStep(FunnelSteps.MAP_INTERACTION_STARTED);
            }
            setActiveTool(tool);
        }
    };

    const handleZoomIn = () => {
        if (olMapRef.current) {
            const view = olMapRef.current.getView();
            const zoom = view.getZoom();
            view.setZoom(zoom ? zoom + 1 : 1);
        }
    };

    const handleZoomOut = () => {
        if (olMapRef.current) {
            const view = olMapRef.current.getView();
            const zoom = view.getZoom();
            view.setZoom(zoom ? zoom - 1 : 1);
        }
    };

    const handleUndoPoint = () => {
        if (drawInteractionRef.current && drawingFeature) {
            // Modo dibujo: deshacer último punto
            drawInteractionRef.current.removeLastPoint();
        }
    };

    const handleUndoEdit = () => {
        const feature = editingFeatureRef.current;
        const stack = undoStackRef.current;
        if (feature && stack.length > 0 && vectorSourceRef.current) {
            const prevGeometry = stack.pop();
            feature.setGeometry(prevGeometry.clone());
            setUndoStackLength(stack.length);
            vectorSourceRef.current.changed();
            const area = calculateArea(feature.getGeometry());
            if (area > 100) {
                setDrawMessage(`Área: ${area.toFixed(2)} ha - Máximo permitido: 100 ha`);
                setDrawMessageError(true);
            } else if (area < 5) {
                setDrawMessage(`Área: ${area.toFixed(2)} ha - Mínimo permitido: 5 ha`);
                setDrawMessageError(true);
            } else {
                setDrawMessage(`Área: ${area.toFixed(2)} ha`);
                setDrawMessageError(false);
            }
        }
    };

    const handleFinishDrawing = () => {
        if (drawInteractionRef.current && drawingFeature && canFinishDrawing) {
            // Finalizar el dibujo (equivalente a hacer click en el primer punto)
            const draw = drawInteractionRef.current;
            // Usar finishDrawing() que está disponible en OpenLayers Draw
            if (draw && typeof draw.finishDrawing === 'function') {
                draw.finishDrawing();
            }
        }
    };

    const handleDelete = () => {
        if (drawingFeature && drawInteractionRef.current) {
            // Si estamos dibujando, cancelar el dibujo y limpiar todo
            drawInteractionRef.current.abortDrawing();
            setDrawingFeature(null);
        }
        
        // Limpiar todo el mapa
        if (vectorSourceRef.current) {
            const allFeatures = vectorSourceRef.current.getFeatures();
            allFeatures.forEach(feature => {
                vectorSourceRef.current.removeFeature(feature);
            });
        }
        
        currentFeatureRef.current = null;
        dispatch({
            type: GIS,
            payload: null
        });
        dispatch({ type: SET_HAS_POLYGON, payload: false });
        
        // Cerrar el popup de confirmación
        setShowDeleteConfirm(false);
        
        // Si la herramienta de dibujo estaba activa, reactivarla para restaurar los mensajes iniciales
        // Primero desactivarla y luego reactivarla para forzar la recreación de la interacción
        if (activeTool === 'polygon') {
            setActiveTool(null);
            // Usar setTimeout para asegurar que el useEffect se ejecute después del cambio
            setTimeout(() => {
                setActiveTool('polygon');
            }, 0);
        } else {
            // Si no estaba activa, simplemente activarla
            setActiveTool('polygon');
        }
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const handleSave = () => {
        const feature = editingFeatureRef.current || currentFeatureRef.current;
        if (feature) {
            const geometry = feature.getGeometry();
            const area = calculateArea(geometry);
            
            if (area > 100) {
                setDrawMessage(`Área: ${area.toFixed(2)} ha - Máximo permitido: 100 ha`);
                setDrawMessageError(true);
                setShowDrawMessage(true);
                dispatch({ type: GIS, payload: null });
                setTimeout(() => {
                    setShowDrawMessage(false);
                    setDrawMessage('');
                    setDrawMessageError(false);
                }, 2000);
            } else if (area < 5) {
                setDrawMessage(`Área: ${area.toFixed(2)} ha - Mínimo permitido: 5 ha`);
                setDrawMessageError(true);
                setShowDrawMessage(true);
                dispatch({ type: GIS, payload: null });
                setTimeout(() => {
                    setShowDrawMessage(false);
                    setDrawMessage('');
                    setDrawMessageError(false);
                }, 2000);
            } else {
                // Guardar los cambios
                checkArea(feature);
                setShowDrawMessage(false);
                setDrawMessage('');
                setDrawMessageError(false);
                setActiveTool(null);
                goToFormAfterConfirm();
            }
        }
    };

    return (
        <div className="mapa-wrapper">
            <div className={`draw-tooltip ${showDrawMessage ? '' : 'hidden'} ${drawMessageError ? 'error' : ''}`}>
                {drawMessage}
            </div>
            <div className={`map-controls-stack${searchExpanded ? ' is-search-expanded' : ''}`}>
                <MapSearch
                    mapRef={olMapRef}
                    onResultSelected={() => setActiveTool('polygon')}
                    onExpandedChange={setSearchExpanded}
                />
                <KmlUploadButton
                    onFileSelect={handleKmlFile}
                    disabled={activeTool === 'edit' || kmlLoading}
                    isLoading={kmlLoading}
                />
            </div>
            <div className="toolbar-container">
                <div className="toolbar-zoom-controls">
                    <button className="toolbar-zoom-button" onClick={handleZoomIn} title="Acercar">
                        +
                    </button>
                    <button className="toolbar-zoom-button" onClick={handleZoomOut} title="Alejar">
                        −
                    </button>
                </div>
                <div className="toolbar-divider"></div>
                {(drawingFeature || activeTool === 'edit') && (
                    <>
                        <button
                            className="toolbar-button toolbar-undo-button"
                            onClick={drawingFeature ? handleUndoPoint : handleUndoEdit}
                            title={drawingFeature ? 'Deshacer último punto' : 'Deshacer última edición'}
                            disabled={activeTool === 'edit' && undoStackLength === 0}
                        >
                            ↶
                        </button>
                        <div className="toolbar-divider"></div>
                        {((drawingFeature && canFinishDrawing && (typeof window !== 'undefined' && window.innerWidth <= 1024)) || activeTool === 'edit') && (
                            <button
                                className={`toolbar-button ${activeTool === 'edit' ? 'toolbar-save-button' : 'toolbar-finish-button'}`}
                                onClick={activeTool === 'edit' ? handleSave : handleFinishDrawing}
                                title={activeTool === 'edit' ? 'Guardar polígono' : 'Confirmar polígono'}
                                disabled={activeTool === 'edit' && drawMessageError}
                            >
                                ✓
                            </button>
                        )}
                          <button
                            className={`toolbar-button toolbar-delete-button ${activeTool === 'delete' ? 'active' : ''}`}
                            onClick={handleDeleteClick}
                            disabled={!vectorSourceRef.current || (!drawingFeature && !hasFeatures)}
                            title="Borrar polígono"
                        >
                            ✕
                        </button>
                        <div className="toolbar-divider"></div>
                    </>
                )}

                <button
                    className={`toolbar-button ${activeTool === 'polygon' ? 'active' : ''}`}
                    onClick={() => handleToolClick('polygon')}
                    disabled={activeTool === 'edit' || hasFeatures}
                    title="Dibujar polígono"
                >
                    ⬟
                </button>
              
                <button
                    className={`toolbar-button ${activeTool === 'edit' ? 'active' : ''}`}
                    onClick={() => handleToolClick('edit')}
                    disabled={!hasFeatures}
                    title="Editar"
                >
                    ✎
                </button>

                <div className="toolbar-divider"></div>

                <button
                    className="toolbar-button"
                    onClick={handleDeleteClick}
                    disabled={!vectorSourceRef.current || !hasFeatures || activeTool === 'edit'}
                    title="Borrar polígono"
                >
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M6 2.5C6 2.22386 6.22386 2 6.5 2H9.5C9.77614 2 10 2.22386 10 2.5V3H12.5C12.7761 3 13 3.22386 13 3.5C13 3.77614 12.7761 4 12.5 4H3.5C3.22386 4 3 3.77614 3 3.5C3 3.22386 3.22386 3 3.5 3H6V2.5ZM4 5H12L11.3333 12.5C11.2985 12.8827 11.1555 13.2034 10.9126 13.4463C10.6697 13.6892 10.349 13.8322 9.96629 13.867L9.9 13.87H6.1C5.71729 13.87 5.39658 13.727 5.15368 13.4841C4.91079 13.2412 4.76776 12.9205 4.73296 12.5378L4.66667 12.5L4 5Z"
                            fill="currentColor"
                        />
                    </svg>
                </button>

            </div>

            {showDeleteConfirm && (
                <div className="delete-confirm-overlay" onClick={() => setShowDeleteConfirm(false)}>
                    <div className="delete-confirm-popup" onClick={(e) => e.stopPropagation()}>
                        <p className="delete-confirm-text">¿Estás seguro eliminar el polígono?</p>
                        <div className="delete-confirm-buttons">
                            <button
                                className="toolbar-button delete-confirm-cancel"
                                onClick={() => setShowDeleteConfirm(false)}
                            >
                                Cancelar
                            </button>
                            <button
                                className="toolbar-button toolbar-delete-button delete-confirm-borrar"
                                onClick={handleDelete}
                                
                            >
                                Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {kmlCandidates.length > 0 && (
                <KmlPolygonPicker
                    candidates={kmlCandidates}
                    truncatedCount={kmlTruncated}
                    onSelect={applyImportedPolygon}
                    onClose={() => {
                        setKmlCandidates([]);
                        setKmlTruncated(0);
                    }}
                />
            )}

            <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
        </div>
    );
};

export default CustomMap;
